/**
 * Escapes a CSV field value by wrapping in double quotes if it contains
 * commas, double quotes, or newlines. Internal double quotes are escaped
 * by doubling them (RFC 4180).
 */
function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Generates a CSV file from headers and row data, then triggers a browser download.
 *
 * @param filename - The download filename (should include .csv extension)
 * @param headers - Array of column header strings
 * @param rows - 2D array of string cell values
 */
export function exportToCsv(filename: string, headers: string[], rows: string[][]): void {
  const headerLine = headers.map(escapeCsvField).join(',');
  const dataLines = rows.map((row) => row.map(escapeCsvField).join(','));
  const csvContent = [headerLine, ...dataLines].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Generates a dated filename for CSV export.
 *
 * @param reportName - The report name (e.g., "members-report")
 * @returns Filename in format: {report-name}-{YYYY-MM-DD}.csv
 */
export function buildCsvFilename(reportName: string): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${reportName}-${yyyy}-${mm}-${dd}.csv`;
}
